import React, { useState, useRef, useEffect } from "react";
import ReactDOM from "react-dom";
import { IconCal, IconChevronLeft, IconChevronRight } from "../icons/Index";
import './Index.css'

const MONTHS_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const DAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

// ── Mask helpers ──────────────────────────────────────────────────────────────
const toDisplay = (digits) => {
  let out = "";
  for (let i = 0; i < digits.length; i++) {
    if (i === 2 || i === 4) out += "/";
    out += digits[i];
  }
  return out;
};

const caretFor = (digitLen) => {
  if (digitLen <= 2) return digitLen;
  if (digitLen <= 4) return digitLen + 1;
  return digitLen + 2;
};

const smartPad = (d) => {
  let r = d;
  if (r.length === 1 && parseInt(r[0], 10) > 1) r = "0" + r;
  if (r.length >= 2 && r.slice(0, 2) === "00") r = "01" + r.slice(2);
  if (r.length >= 2 && parseInt(r.slice(0, 2), 10) > 12) r = "12" + r.slice(2);
  if (r.length === 3 && parseInt(r[2], 10) > 3) r = r.slice(0, 2) + "0" + r[2];
  if (r.length >= 4 && r.slice(2, 4) === "00") r = r.slice(0, 2) + "01" + r.slice(4);
  if (r.length >= 4 && parseInt(r.slice(2, 4), 10) > 31) r = r.slice(0, 2) + "31" + r.slice(4);
  return r;
};

// ── LoginDatePicker ───────────────────────────────────────────────────────────
const LoginDatePicker = ({ value, onChange, hasError }) => {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("days");
  const [dropPos, setDropPos] = useState({ top: 0, left: 0, width: 240 });
  const [digits, setDigits] = useState("");
  const [viewDate, setViewDate] = useState(() => {
    if (value) {
      const d = new Date(value + "T00:00:00");
      return { month: d.getMonth(), year: d.getFullYear() };
    }
    const t = new Date();
    return { month: t.getMonth(), year: t.getFullYear() };
  });

  const triggerRef = useRef(null);
  const dropRef = useRef(null);
  const inputRef = useRef(null);
  const yearListRef = useRef(null);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const selectedDate = value ? new Date(value + "T00:00:00") : null;

  // Sync digits when value changes externally
  useEffect(() => {
    if (value) {
      const d = new Date(value + "T00:00:00");
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const dd = String(d.getDate()).padStart(2, "0");
      const yyyy = String(d.getFullYear());
      setDigits(mm + dd + yyyy);
      setViewDate({ month: d.getMonth(), year: d.getFullYear() });
    } else {
      setDigits("");
    }
  }, [value]);

  // Keep caret at end
  useEffect(() => {
    const el = inputRef.current;
    if (!el || document.activeElement !== el) return;
    const pos = caretFor(digits.length);
    requestAnimationFrame(() => el.setSelectionRange(pos, pos));
  }, [digits]);

  // Scroll selected year into view when year picker opens
  useEffect(() => {
    if (mode === "years" && yearListRef.current) {
      const selected = yearListRef.current.querySelector(".dp-year-item.dp-cell--selected");
      if (selected) {
        requestAnimationFrame(() =>
          selected.scrollIntoView({ block: "center", behavior: "instant" })
        );
      }
    }
  }, [mode]);

  // ── Commit ────────────────────────────────────────────────────────────────
  const tryCommit = (d) => {
    if (d.length < 8) { onChange(""); return; }
    const mm = d.slice(0, 2);
    const dd = d.slice(2, 4);
    const yyyy = d.slice(4, 8);
    const month = parseInt(mm, 10);
    const day = parseInt(dd, 10);
    const year = parseInt(yyyy, 10);
    if (month >= 1 && month <= 12 && day >= 1 && day <= 31 && year >= 1900) {
      const date = new Date(`${yyyy}-${mm}-${dd}T00:00:00`);
      if (
        date.getFullYear() === year &&
        date.getMonth() === month - 1 &&
        date.getDate() === day
        // Manually typed dates are allowed even if in the future;
        // only the calendar picker blocks future dates.
      ) {
        onChange(`${yyyy}-${mm}-${dd}`);
        setViewDate({ month: month - 1, year });
        return;
      }
    }
    onChange("");
  };

  // ── Key handler ───────────────────────────────────────────────────────────
  const handleKeyDown = (e) => {
    if (e.key === "Escape") { setOpen(false); setMode("days"); return; }
    if (e.key === "Tab") { setOpen(false); setMode("days"); return; }
    if (e.key === "ArrowDown" && !open) { e.preventDefault(); openCalendar(); return; }

    if (e.key === "Backspace") {
      e.preventDefault();
      setDigits(prev => {
        const next = prev.slice(0, -1);
        if (next.length === 8) tryCommit(next);
        else onChange("");
        return next;
      });
      return;
    }
    if (e.key === "Delete") { e.preventDefault(); setDigits(""); onChange(""); return; }
    if (!/^\d$/.test(e.key)) { if (!((e.ctrlKey || e.metaKey) && e.key === "v")) e.preventDefault(); return; }

    e.preventDefault();
    setDigits(prev => {
      if (prev.length >= 8) return prev;
      const next = smartPad(prev + e.key);
      tryCommit(next);
      return next;
    });
  };

  const handleChange = (e) => e.preventDefault();

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = (e.clipboardData.getData("text") || "").trim();
    let mm, dd, yyyy;
    const isoMatch = pasted.match(/^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})$/);
    const mdyMatch = pasted.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{4})$/);
    const rawDigits = pasted.replace(/\D/g, "");
    if (isoMatch) { [, yyyy, mm, dd] = isoMatch; mm = mm.padStart(2, "0"); dd = dd.padStart(2, "0"); }
    else if (mdyMatch) { [, mm, dd, yyyy] = mdyMatch; mm = mm.padStart(2, "0"); dd = dd.padStart(2, "0"); }
    else if (rawDigits.length === 8) { mm = rawDigits.slice(0, 2); dd = rawDigits.slice(2, 4); yyyy = rawDigits.slice(4, 8); }
    else if (rawDigits.length > 0) {
      // Partial paste: feed up to 8 digits through smartPad
      const clamped = rawDigits.slice(0, 8);
      const nd = smartPad(clamped);
      setDigits(nd);
      tryCommit(nd);
      return;
    }
    if (mm && dd && yyyy) {
      const nd = smartPad(mm + dd + yyyy);
      setDigits(nd);
      tryCommit(nd);
    }
  };

  // ── Position ──────────────────────────────────────────────────────────────
  const computePos = () => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const dpW = Math.max(rect.width, 240);
    const dpH = dropRef.current?.offsetHeight || 260;
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;

    // Prefer opening directly below the input; only flip above if space below cannot fit and above has more room
    const openAbove = spaceBelow < dpH + 10 && spaceAbove > spaceBelow;

    let top = openAbove ? rect.top - dpH - 4 : rect.bottom + 4;

    if (top + dpH > window.innerHeight - 8) top = window.innerHeight - dpH - 8;
    if (top < 8) top = 8;

    let left = rect.left;
    if (left + dpW > window.innerWidth - 8) left = window.innerWidth - dpW - 8;
    if (left < 8) left = 8;

    setDropPos({ top, left, width: dpW });
  };

  const openCalendar = () => { computePos(); setOpen(true); };

  // Re-compute position once rendered to use actual DOM offsetHeight
  useEffect(() => {
    if (open) {
      computePos();
    }
  }, [open, mode]); // eslint-disable-line

  // Reposition on scroll / resize while open
  useEffect(() => {
    if (!open) return;
    const u = () => computePos();
    window.addEventListener("scroll", u, true);
    window.addEventListener("resize", u);
    return () => {
      window.removeEventListener("scroll", u, true);
      window.removeEventListener("resize", u);
    };
  }, [open]); // eslint-disable-line

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target) &&
        triggerRef.current && !triggerRef.current.contains(e.target)) {
        setOpen(false); setMode("days");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  // ── Calendar grid ─────────────────────────────────────────────────────────
  const firstDay = new Date(viewDate.year, viewDate.month, 1).getDay();
  const daysInMonth = new Date(viewDate.year, viewDate.month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const selectDay = (day) => {
    if (!day) return;
    const mm = String(viewDate.month + 1).padStart(2, "0");
    const dd = String(day).padStart(2, "0");
    onChange(`${viewDate.year}-${mm}-${dd}`);
    setOpen(false); setMode("days");
  };

  const prevMonth = () => setViewDate(v => ({
    month: v.month === 0 ? 11 : v.month - 1,
    year: v.month === 0 ? v.year - 1 : v.year,
  }));
  const nextMonth = () => setViewDate(v => ({
    month: v.month === 11 ? 0 : v.month + 1,
    year: v.month === 11 ? v.year + 1 : v.year,
  }));

  const currentYear = today.getFullYear();
  const yearList = [];
  for (let y = currentYear; y >= currentYear - 150; y--) yearList.push(y);

  const handleIconClick = () => {
    if (open) { setOpen(false); setMode("days"); }
    else { openCalendar(); inputRef.current?.focus(); }
  };

  // ── Dropdown portal ───────────────────────────────────────────────────────
  const dropdown = open && ReactDOM.createPortal(
    <div
      ref={dropRef}
      className="dp-dropdown"
      style={{
        position: "fixed",
        top: dropPos.top,
        left: dropPos.left,
        width: dropPos.width,
        zIndex: 99999,
      }}
    >
      {/* ── DAYS VIEW ── */}
      {mode === "days" && (
        <>
          <div className="dp-header">
            <button type="button" className="dp-nav-btn" onClick={prevMonth}><IconChevronLeft /></button>

            <div className="dp-header-pills">
              <button type="button" className="dp-pill dp-pill--month"
                onClick={() => setMode("months")} title="Pick month">
                {MONTHS_SHORT[viewDate.month]}
                <span className="dp-pill-caret">▾</span>
              </button>
              <button type="button" className="dp-pill dp-pill--year"
                onClick={() => setMode("years")} title="Pick year">
                {viewDate.year}
                <span className="dp-pill-caret">▾</span>
              </button>
            </div>

            <button type="button" className="dp-nav-btn" onClick={nextMonth}><IconChevronRight /></button>
          </div>

          <div className="dp-day-names">
            {DAYS.map(d => <span key={d} className="dp-day-name">{d}</span>)}
          </div>

          <div className="dp-grid">
            {cells.map((day, i) => {
              if (!day) return <span key={`e${i}`} className="dp-cell dp-cell--empty" />;
              const thisDate = new Date(viewDate.year, viewDate.month, day);
              thisDate.setHours(0, 0, 0, 0);
              const isFuture = thisDate > today;
              const isSelected = selectedDate &&
                selectedDate.getDate() === day &&
                selectedDate.getMonth() === viewDate.month &&
                selectedDate.getFullYear() === viewDate.year;
              const isToday = thisDate.getTime() === today.getTime();
              return (
                <button key={day} type="button"
                  className={[
                    "dp-cell",
                    isSelected ? "dp-cell--selected" : "",
                    isToday && !isSelected ? "dp-cell--today" : "",
                    isFuture ? "dp-cell--disabled" : "",
                  ].join(" ").trim()}
                  onClick={() => !isFuture && selectDay(day)}
                  disabled={isFuture}
                >{day}</button>
              );
            })}
          </div>
        </>
      )}

      {/* ── MONTHS VIEW ── */}
      {mode === "months" && (
        <>
          <div className="dp-header dp-header--compact">
            <button type="button" className="dp-back-btn" onClick={() => setMode("days")}>
              <IconChevronLeft /> <span>Back</span>
            </button>
            <span className="dp-header-label">
              <button type="button" className="dp-pill dp-pill--year"
                onClick={() => setMode("years")} title="Pick year">
                {viewDate.year}
                <span className="dp-pill-caret">▾</span>
              </button>
            </span>
          </div>

          <div className="dp-month-grid">
            {MONTHS_SHORT.map((m, i) => (
              <button key={m} type="button"
                className={["dp-month-cell", viewDate.month === i ? "dp-cell--selected" : ""].join(" ").trim()}
                onClick={() => { setViewDate(v => ({ ...v, month: i })); setMode("days"); }}
              >{m}</button>
            ))}
          </div>
        </>
      )}

      {/* ── YEARS VIEW ── */}
      {mode === "years" && (
        <>
          <div className="dp-header dp-header--compact">
            <button type="button" className="dp-back-btn" onClick={() => setMode("days")}>
              <IconChevronLeft /> <span>Back</span>
            </button>
            <span className="dp-header-label">Select Year</span>
          </div>

          <div className="dp-year-grid" ref={yearListRef}>
            {yearList.map(y => (
              <button key={y} type="button"
                className={["dp-year-item", viewDate.year === y ? "dp-cell--selected" : ""].join(" ").trim()}
                onClick={() => { setViewDate(v => ({ ...v, year: y })); setMode("days"); }}
              >{y}</button>
            ))}
          </div>
        </>
      )}
    </div>,
    document.body
  );

  return (
    <div className="dp-wrap" ref={triggerRef}>
      <div className="login-input-wrap">
        <span className="login-input-icon"
          style={{ color: hasError ? "#e05252" : undefined, cursor: "pointer" }}
          onClick={handleIconClick}
        >
          <IconCal />
        </span>
        <input
          ref={inputRef}
          className={
            hasError
              ? "login-input-error"
              : `login-input-dob${digits ? " login-input-dob--filled" : " login-input-dob--empty"}`
          }
          type="text"
          placeholder="MM/DD/YYYY"
          value={toDisplay(digits)}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          onFocus={openCalendar}
          maxLength={10}
          inputMode="numeric"
          autoComplete="off"
        />
      </div>
      {dropdown}
    </div>
  );
};

export default LoginDatePicker;